/**
 * P3: Standardised lesson-status badge + detail drawer.
 *
 * Backend resolves each calendar event's `lesson_status` to one of:
 *   scheduled · rescheduled · no_class_teacher_leave · no_class_teacher_mc
 *   no_class_teacher_el · no_class_student_leave · replacement_pending
 *   replacement_confirmed · partially_replaced · fully_replaced · group_affected
 *   cancelled
 *
 * These components render the label consistently across teacher/parent/admin
 * views and open a drawer with full linked-info when clicked.
 */
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/common";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { CalendarOff, RefreshCcw, Check, Clock, Users, Info, Plane } from "lucide-react";

export const LESSON_STATUS_META = {
  scheduled:              { label: "Scheduled",           cls: "bg-emerald-100 text-emerald-900 border-emerald-300", icon: Check },
  rescheduled:            { label: "Rescheduled",         cls: "bg-teal-100 text-teal-900 border-teal-300", icon: RefreshCcw },
  no_class_teacher_leave: { label: "No Class — Teacher Leave", cls: "bg-zinc-100 text-zinc-700 border-zinc-300", icon: CalendarOff },
  no_class_teacher_mc:    { label: "No Class — Teacher MC",    cls: "bg-rose-100 text-rose-900 border-rose-300", icon: CalendarOff },
  no_class_teacher_el:    { label: "No Class — Teacher EL",    cls: "bg-red-100 text-red-900 border-red-300", icon: CalendarOff },
  no_class_student_leave: { label: "No Class — Student Leave", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: CalendarOff },
  replacement_pending:    { label: "Replacement Pending",      cls: "bg-amber-100 text-amber-900 border-amber-300", icon: Clock },
  replacement_confirmed:  { label: "Replacement Confirmed",    cls: "bg-teal-100 text-teal-900 border-teal-300", icon: Check },
  partially_replaced:     { label: "Partially Replaced",       cls: "bg-yellow-100 text-yellow-900 border-yellow-300", icon: RefreshCcw },
  fully_replaced:         { label: "Fully Replaced",           cls: "bg-lime-100 text-lime-900 border-lime-300", icon: Check },
  group_affected:         { label: "Group Class Affected",     cls: "bg-orange-100 text-orange-900 border-orange-300", icon: Users },
  cancelled:              { label: "Cancelled Class",          cls: "bg-zinc-100 text-zinc-500 border-zinc-300 line-through", icon: CalendarOff },
};

export function LessonStatusBadge({ status, className = "" }) {
  const meta = LESSON_STATUS_META[status] || LESSON_STATUS_META.scheduled;
  const Icon = meta.icon;
  return (
    <Badge variant="outline"
      className={`inline-flex items-center gap-1 border font-mono text-[10px] px-1.5 py-0.5 ${meta.cls} ${className}`}
      data-testid={`lesson-status-${status}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}

export function LessonDetailDrawer({ classScheduleId, date, open, onOpenChange, role = "teacher" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    if (!open || !classScheduleId || !date) return;
    setLoading(true); setData(null);
    api.get("/lessons/detail", { params: { class_schedule_id: classScheduleId, date } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [open, classScheduleId, date]);

  const ack = async () => {
    if (!data?.leave?.notice?.id) return;
    setAcking(true);
    try {
      await api.post(`/leave-notices/${data.leave.notice.id}/acknowledge`);
      toast.success("Notice acknowledged");
      const { data: fresh } = await api.get("/lessons/detail", {
        params: { class_schedule_id: classScheduleId, date }});
      setData(fresh);
    } catch (e) { toast.error(errMsg(e)); } finally { setAcking(false); }
  };

  const meta = LESSON_STATUS_META[data?.lesson_status] || LESSON_STATUS_META.scheduled;
  const sch = data?.class_schedule || {};
  const leave = data?.leave;
  const rr = data?.reschedule_request;
  const rb = data?.replacement_booking;
  const ml = data?.minute_ledger;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto"
        data-testid="lesson-detail-drawer">
        <SheetHeader className="space-y-1">
          <SheetTitle className="font-display flex items-center gap-2 flex-wrap">
            Lesson detail
            <LessonStatusBadge status={data?.lesson_status || "scheduled"} />
          </SheetTitle>
          <SheetDescription>
            {sch.student_name || "—"} · {sch.course_name || ""} · {date} {sch.start_time}–{sch.end_time}
          </SheetDescription>
        </SheetHeader>
        {loading ? <Spinner /> : !data ? null : (
          <div className="space-y-4 mt-4 text-sm">
            {data.lesson_status_reason && (
              <p className="text-xs text-muted-foreground flex items-start gap-1.5"
                data-testid="lesson-status-reason">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                {data.lesson_status_reason}
              </p>
            )}
            <div className="rounded-md border bg-card p-3 space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Original class</p>
              <p className="font-mono text-xs">
                {sch.start_time}–{sch.end_time} · {sch.studio_name || "no studio"}
              </p>
              <p className="text-xs">Teacher: {sch.teacher_name}</p>
              <p className="text-xs">Type: {sch.class_type || "individual"}{sch.group_size ? ` (${sch.group_size})` : ""}</p>
            </div>
            {leave && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 space-y-1"
                data-testid="linked-leave">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <Plane className="h-3 w-3" /> Linked leave
                </p>
                <p className="text-xs">
                  {leave.leave_category?.toUpperCase()} · {leave.leave_date}
                  {leave.reason ? ` · ${leave.reason}` : ""}
                </p>
                {leave.notice && (
                  <div className="mt-1.5 text-[11px] flex flex-wrap items-center gap-1.5">
                    {leave.notice.acknowledged ? (
                      <Badge className="bg-emerald-600" data-testid="notice-acked">
                        Ack ✓ {leave.notice.acknowledged_by_name ? `by ${leave.notice.acknowledged_by_name}` : ""}
                      </Badge>
                    ) : (
                      <Badge variant="destructive" data-testid="notice-not-acked">
                        Not acknowledged
                      </Badge>
                    )}
                    {leave.notice.escalated && (
                      <Badge variant="destructive" className="animate-pulse">Escalated</Badge>
                    )}
                    {leave.notice.manual_followup_result && (
                      <Badge variant="secondary">
                        Follow-up: {leave.notice.manual_followup_result}
                      </Badge>
                    )}
                    {role === "parent" && !leave.notice.acknowledged && (
                      <Button size="sm" className="h-7 ml-auto"
                        onClick={ack} disabled={acking}
                        data-testid="ack-notice-button">
                        {acking ? "..." : "Acknowledge notice"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
            {rr && (
              <div className="rounded-md border border-teal-200 bg-teal-50 p-3 space-y-1"
                data-testid="linked-reschedule">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <RefreshCcw className="h-3 w-3" /> Linked replacement
                </p>
                <p className="text-xs">Status: <span className="font-mono">{rr.status}</span></p>
                {rr.final_agreed_date && (
                  <p className="text-xs font-mono">
                    → {rr.final_agreed_date} {rr.final_agreed_start_time}–{rr.final_agreed_end_time}
                    {rr.final_agreed_studio_name ? ` · ${rr.final_agreed_studio_name}` : ""}
                  </p>
                )}
                {rb && (
                  <p className="text-[10px] text-muted-foreground">
                    Booking id: {rb.id?.slice(0, 8)} · status {rb.status}
                  </p>
                )}
              </div>
            )}
            {ml && (ml.owed > 0 || ml.completed > 0) && (
              <div className="rounded-md border bg-card p-3 space-y-1" data-testid="minute-ledger">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Minute ledger</p>
                <div className="flex items-center gap-3 text-xs font-mono">
                  <span>Owed: {ml.owed}</span>
                  <span>Completed: {ml.completed}</span>
                  <span className={ml.remaining === 0 ? "text-emerald-700 font-bold" : "text-amber-700 font-bold"}>
                    Remaining: {ml.remaining}
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${ml.owed ? Math.min(100, (ml.completed / ml.owed) * 100) : 0}%` }} />
                </div>
              </div>
            )}
            {data.attendance && (
              <div className="rounded-md border bg-card p-3 space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Attendance</p>
                <p className="text-xs">Status: {data.attendance.student_status}</p>
                {data.attendance.notes && <p className="text-xs text-muted-foreground">{data.attendance.notes}</p>}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
